import { FieldSet, LazyRender } from "@prismicio/editor-ui"
import type { SharedSliceContent } from "@prismicio/types-internal/lib/content"
import type {
  SharedSlice,
  Variation,
} from "@prismicio/types-internal/lib/customtypes"
import { useId, useMemo } from "react"

import { type EditorConfig, EditorConfigContext } from "../EditorConfig"
import { FieldInSliceContextProvider } from "../FieldContext"
import { getSliceSelectionDOMId, useSliceSelection } from "../SliceSelection"
import { RepeatableZoneEditor } from "../zones/RepeatableZoneEditor"
import { SlicePrimaryZoneEditor } from "../zones/SlicePrimaryZoneEditor"

// Note that this component is used both by wroom (via DocumentEditor) and Slice Machine (as-is).

interface Props {
  config: EditorConfig
  content: SharedSliceContent
  id?: string
  /**
   * Used by wroom to visually select and scroll to a slice.
   */
  sliceKey?: string
  onContentChange: (content: SharedSliceContent) => void
  readOnly?: boolean
  sharedSlice: SharedSlice
}

export function SharedSliceEditor(props: Props) {
  const {
    config,
    content,
    sliceKey,
    readOnly = false,
    sharedSlice,
    onContentChange,
    ...otherProps
  } = props

  const variation = useVariation(sharedSlice, content.variation)
  const defaultId = useId()

  const id = otherProps.id ?? defaultId
  const zoneProps = { id, readOnly }
  const selection = useSliceSelection()

  function onFocus() {
    if (!sliceKey) return
    selection.update({ id: sliceKey, requestedFrom: "sliceFieldSet" })
  }

  if (variation === undefined) return null

  return (
    <EditorConfigContext.Provider value={config}>
      <FieldInSliceContextProvider>
        <FieldSet
          id={sliceKey && getSliceSelectionDOMId("sliceFieldSet", sliceKey)}
          selected={sliceKey ? selection.isSelected(sliceKey) : false}
          onFocus={onFocus}
          legend={getLegend(sharedSlice, variation)}
        >
          {/* This gap is equal to the gap in the FieldSet component theme (theme.space.fieldSetGap) */}
          <LazyRender
            heightEstimate={400}
            gap={16}
            keepLoaded
            loadWhenSearching
          >
            <SlicePrimaryZoneEditor
              {...zoneProps}
              slicePrimaryZone={variation.primary ?? {}}
              content={content.primary}
              onContentChange={(nonRepeatableZoneContent) => {
                onContentChange({
                  ...content,
                  primary: nonRepeatableZoneContent,
                })
              }}
            />
          </LazyRender>

          <RepeatableZoneEditor
            {...zoneProps}
            repeatableZone={variation.items ?? {}}
            content={content.items}
            onContentChange={(repeatableZoneContent) => {
              onContentChange({ ...content, items: repeatableZoneContent })
            }}
          />
        </FieldSet>
      </FieldInSliceContextProvider>
    </EditorConfigContext.Provider>
  )
}

function useVariation(
  sharedSlice: SharedSlice,
  variationId: string,
): Variation | undefined {
  return useMemo(
    () =>
      sharedSlice.variations.find((variation) => variation.id === variationId),
    [sharedSlice, variationId],
  )
}

function getLegend(sharedSlice: SharedSlice, variation: Variation): string {
  return [sharedSlice.name, variation.name].join(" • ")
}
